<script setup>
  import { ref, computed, watchEffect, onErrorCaptured } from 'vue'
  import { useUserStore } from "@/stores/UserStore";
  import { useSpotStore } from "@/stores/SpotStore";
  import DataContainerSection from '@/components/DataContainerSection.vue';
  import ModalSaveContainerToProfile from '@/components/ModalSaveContainerToProfile.vue';
  import Popup from '@/components/controls/Popup.vue';
  import ErrorBlock from '@/components/ErrorBlock.vue';
  import DataContainer from '@/components/DataContainer.vue';
  import Icon from "@/components/icons/Icon.vue";

  import useSettingsGlobals from '@/composables/useSettingsGlobals';

  const { $WEB_VIEW } = useSettingsGlobals();   

  const content = defineModel('content');

  const props = defineProps({
    title: {
      type: String,
      required: true
    },
    sectionId: {
      type: String,
      required: true
    },
    containerTypeId: {
      type: String,
      required: false
    },
    containerTypeName: {
      type: String,
      required: false
    },
    minHeight: {
      type: String,
      required: false
    },
    contentClass: {
      type: String,
      required: false
    },
    metaActions: {
      type: Boolean,
      default: true
    },
    alwaysShowMetaActions: {
      type: Boolean,
      default: false
    }
  });

  const userStore = useUserStore();
  const spotStore = useSpotStore();

  const container = ref(null);
  const showModal = ref(false);
  const closePopup = ref(false)
  const error = ref(null);

  const containerTypeName = computed(() => props.containerTypeName || props.containerTypeId);
  const settings = computed(() => content.value.getSettings());
  const id = computed(() => `${props.containerTypeId.trim().toLocaleLowerCase()}-${spotStore.spotId}`.split(' ').join('-'))

  function toggleModal() {
    showModal.value = !showModal.value;
  }

  function handleSaveContainerType() {
    if (!props.containerTypeId) return;

    toggleModal();
  }

  watchEffect(() => {
    if (!showModal.value) {
      closePopup.value = true;
    }

    closePopup.value = showModal.value ? true : false;
  });

  onErrorCaptured((e) => {
    console.warn(e);

    error.value = { 
      title: `There was a problem loading the ${props.title}`,
      message: `Please try again later.`,
      errorText: e.message
    }

    return false;
  })   
</script>

<template>
    <DataContainerSection
      v-model:container="container"
      :lazy="true"
      :id="props.sectionId"
      :minHeight="props.minHeight"
      :contentClass="props.contentClass"
    >
      <template #title>{{ props.title }}</template>
      <template v-if="props.metaActions && !$WEB_VIEW || props.metaActions && props.alwaysShowMetaActions" #metaActions>
        <slot name="metaActions">
          <Popup
            :id="id"
            icon="more-menu"
            :closePopup="closePopup"
            :hideDivider="true"
          >
            <div class="jwx-action-menu">
              <ul class="jwx-action-menu__list">
                <li v-if="userStore.memberLevel > 0" class="jwx-action-menu__action">
                  <button 
                    type="button"
                    @click.prevent="handleSaveContainerType"
                    class="jwx-action-menu__action-button"
                  >
                    <Icon name="star" class="jwx-action-menu__action-icon" />
                    <span class=jwx-action-menu__action-label>Save {{ containerTypeName }}</span>
                  </button>
                </li>
              </ul>
            </div>
          </Popup>
          <ModalSaveContainerToProfile
            v-if="showModal"
            v-model:showModal="showModal"
            :containerTypeId="props.containerTypeId"
            :containerTypeName="containerTypeName"
            :settings="settings"
          />
        </slot>
      </template>
      <template #content="{ isVisible }">
        <DataContainer v-if="error" style="width: 100%; height: 100%;">
          <ErrorBlock :error="error" />
        </DataContainer>
        <slot v-else name="content" :isVisible="isVisible"></slot>
      </template>
    </DataContainerSection>
</template>